import { Component, Suspense, lazy, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence, Variants, useReducedMotion } from 'framer-motion';
import { theme } from '@/styles/theme';
import { Button } from '@/components/ui/Button';
import { WELCOME_VISITED_KEY } from '@/constants';
import { StaticBackdrop } from './welcome/StaticBackdrop';
import { LanguageSelect } from './welcome/LanguageSelect';
import { useWelcomeStore } from './welcome/useWelcomeStore';

// The Three.js layer is a client-only, lazily imported leaf (Vite's equivalent
// of dynamic import with ssr:false) so three/R3F stay out of the main bundle.
const RibbonScene = lazy(() => import('./welcome/RibbonScene'));

// --- WebGL capability probe ---

function canRenderWebGL(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
}

// If the canvas chunk fails to load or the scene throws, fall back to the
// static backdrop rather than blanking the screen.
class CanvasBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

// --- Styled Components ---

const WelcomeWrapper = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: var(--kph-welcome-bg, #ffffff);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const SceneLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

// Warm footlights rising from the bottom edge, like the front of a stage.
const Footlights = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: radial-gradient(
    90% 38% at 50% 102%,
    rgba(212, 175, 55, 0.2) 0%,
    rgba(212, 175, 55, 0.07) 45%,
    rgba(212, 175, 55, 0) 100%
  );
`;

const Content = styled(motion.div)`
  position: relative;
  z-index: 3;
  width: 92%;
  max-width: 720px;
  text-align: center;
  padding: ${theme.spacing[8]};
  pointer-events: none; /* let the cursor drive the ribbons; CTA re-enables below */

  /* Soft white scrim so ribbons passing behind never cost readability. */
  &::before {
    content: '';
    position: absolute;
    inset: -18% -8%;
    background: radial-gradient(
      ellipse at center,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0.55) 45%,
      rgba(255, 255, 255, 0) 72%
    );
    z-index: -1;
  }
`;

const Kicker = styled(motion.span)`
  display: block;
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.sm};
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: var(--kph-navy-ink, #1a1a2e);
  font-weight: ${theme.fontWeights.semibold};
  margin-bottom: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.fontSizes.xs};
    letter-spacing: 0.18em;
    margin-bottom: ${theme.spacing[4]};
  }
`;

const Title = styled(motion.h1)`
  font-family: ${theme.fonts.decorative};
  font-size: clamp(3.25rem, 11vw, 7rem);
  font-weight: 600;
  line-height: 1.05;
  letter-spacing: 0.01em;
  color: var(--kph-navy-ink, #1a1a2e);
  margin: 0;
`;

// Brand motif shared with the homepage: a short gold rule under the title.
const GoldRule = styled(motion.span)`
  display: block;
  width: 48px;
  height: 3px;
  margin: ${theme.spacing[5]} auto 0;
  background: var(--kph-gold, #d4af37);
`;

const SubLine = styled(motion.p)`
  font-family: ${theme.fonts.body};
  font-size: clamp(1.05rem, 2.2vw, 1.25rem);
  line-height: 1.7;
  color: var(--kph-navy-ink, #1a1a2e);
  opacity: 0.82;
  margin: ${theme.spacing[5]} auto ${theme.spacing[10]};

  @media (max-width: ${theme.breakpoints.md}) {
    margin-bottom: ${theme.spacing[8]};
  }
`;

const CtaWrap = styled(motion.div)`
  position: relative;
  display: inline-block;
  pointer-events: auto;
`;

// Soft spotlight that appears behind the CTA on hover.
const Spotlight = styled(motion.span)`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 320px;
  height: 320px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(212, 175, 55, 0.4) 0%, rgba(212, 175, 55, 0) 70%);
  pointer-events: none;
  z-index: -1;
`;

const CtaButton = styled(motion.create(Button))`
  letter-spacing: 0.03em;

  @media (max-width: ${theme.breakpoints.sm}) {
    width: 100%;
  }
`;

// --- Motion Variants ---

const buildVariants = (reduced: boolean) => {
  const container: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: reduced ? 0 : 0.12, delayChildren: reduced ? 0 : 0.2 },
    },
    exit: { opacity: 0, transition: { duration: reduced ? 0.4 : 0.8, ease: [0.76, 0, 0.24, 1] } },
  };

  const item: Variants = {
    hidden: reduced ? { opacity: 0 } : { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: reduced ? 0.3 : 0.7, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return { container, item };
};

// --- Main Component ---

interface WelcomeScreenProps {
  onComplete?: () => void;
}

export const WelcomeScreen = ({ onComplete }: WelcomeScreenProps) => {
  const [showWelcome, setShowWelcome] = useState(false);
  const [entering, setEntering] = useState(false);
  const [hovering, setHovering] = useState(false);
  const reduced = useReducedMotion() ?? false;
  const [use3D] = useState(() => !reduced && canRenderWebGL());
  const setPhase = useWelcomeStore((s) => s.setPhase);
  const finishedRef = useRef(false);
  const safetyTimer = useRef<number | undefined>(undefined);

  const { container, item } = buildVariants(reduced);

  // Track the latest onComplete in a ref so no effect (or its cleanup) keys on
  // the prop's identity. A parent re-render mid-transition must never clear
  // the safety timer or restart the fly-through (that froze the overlay).
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  });

  useEffect(() => {
    const hasVisited = localStorage.getItem(WELCOME_VISITED_KEY);
    if (!hasVisited) {
      document.body.style.overflow = 'hidden';
      setShowWelcome(true);
    } else {
      onCompleteRef.current?.();
    }
    return () => window.clearTimeout(safetyTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only by design
  }, []);

  // Stable: passed to the 3D scene as onEntered; its effect must not re-fire.
  const finish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    window.clearTimeout(safetyTimer.current);
    // Also restored in onExitComplete; doing it here too means the site can
    // never stay scroll-locked if the exit animation is interrupted.
    document.body.style.overflow = '';
    setShowWelcome(false);
  }, []);

  const handleEnter = () => {
    if (entering) return;
    setEntering(true);
    localStorage.setItem(WELCOME_VISITED_KEY, 'true');
    onCompleteRef.current?.(); // reveal the site beneath before the fly-through

    if (use3D) {
      setPhase('enter'); // FlyThrough drives the camera, then calls finish()
      safetyTimer.current = window.setTimeout(finish, 1600); // safety net
    } else {
      window.setTimeout(finish, 220);
    }
  };

  const handleHover = (next: boolean) => {
    if (entering) return;
    setHovering(next);
    setPhase(next ? 'hover' : 'intro');
  };

  const handleExitComplete = () => {
    document.body.style.overflow = '';
  };

  return (
    <AnimatePresence mode="wait" onExitComplete={handleExitComplete}>
      {showWelcome && (
        <WelcomeWrapper
          key="welcome-modal"
          variants={container}
          initial="hidden"
          animate="visible"
          exit="exit"
          /* From the moment the visitor enters, the overlay must never eat
             clicks meant for the site beneath, even mid-exit. */
          style={{ pointerEvents: entering ? 'none' : 'auto' }}
        >
          <SceneLayer>
            {use3D ? (
              <CanvasBoundary fallback={<StaticBackdrop />}>
                <Suspense fallback={<StaticBackdrop />}>
                  <RibbonScene onEntered={finish} />
                </Suspense>
              </CanvasBoundary>
            ) : (
              <StaticBackdrop />
            )}
          </SceneLayer>

          <Footlights aria-hidden="true" />

          <motion.div animate={{ opacity: entering ? 0 : 1 }} transition={{ duration: 0.3 }}>
            <LanguageSelect />
          </motion.div>

          <Content animate={{ opacity: entering ? 0 : 1 }} transition={{ duration: 0.4 }}>
            <Kicker variants={item}>Koninklijke Poperingse Harmonie Sint-Cecilia</Kicker>

            <Title variants={item}>Welkom</Title>

            <GoldRule variants={item} aria-hidden="true" />

            <SubLine variants={item}>Bij onze harmonie</SubLine>

            <motion.div variants={item}>
              <CtaWrap
                onHoverStart={() => handleHover(true)}
                onHoverEnd={() => handleHover(false)}
              >
                <Spotlight
                  animate={{ opacity: hovering ? 1 : 0, scale: hovering ? 1 : 0.7 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                />
                <CtaButton
                  size="lg"
                  variant="secondary"
                  onClick={handleEnter}
                  whileTap={reduced ? undefined : { scale: 0.97 }}
                >
                  Neem me mee
                </CtaButton>
              </CtaWrap>
            </motion.div>
          </Content>
        </WelcomeWrapper>
      )}
    </AnimatePresence>
  );
};
